import * as React from "react";
import Modal from "../../misc/components/modal";
import { connect } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import qs from "query-string";
import {
    FetchCoughAnalysisSetTypes,
} from "../CoughAnalysisActions";
import ImportEventsWizard from "../import-events/ImportEventsWizard";
import ManuallySendForQAModal from "../ManuallySendForQAModal/ManuallySendForQAModal";
import { AssignToUserModal } from "../AssignToUserModal/AssignToUserModal";
import { Authorization } from "../../misc/components/Authorization";
import { AnalysisAcceptabilityStatus } from "../../misc/components/analysis/AnalysisAcceptabilityStatus";
import Condition from "../../misc/components/Condition";
import { User, CoughSessionStatusEnum, OverreadStatusIdEnum, AnalystDecisionStatus } from "../../misc/api/apiTypes";
import { PermissionEnum } from "../../misc/api/ApiPermissionEnum";
import Loading, { FontSize } from "../../misc/components/loading";
import { MainState } from "../../Core/reducers";
import { formatNull, formatDateTime } from "../../misc/components/Formatters/Formatters";
import {
    CoughAnalysisQueryTableProps
} from "./CoughAnalysisQueryTable.interfaces";
import { HistoryView } from "../HistoryView/HistoryView";
import uniqBy from "lodash-es/uniqBy";
import CreateLinkedModal from "../LinkedQAAnalysisSetModal/CreateLinkedModal";
import RemoveLinkedModal from '../LinkedQAAnalysisSetModal/RemoveLinkedModal';
import ResetReviewModal from "./CoughReview/ResetReviewModal/ResetReviewModal";
import EditReviewCompleteModal from "./CoughReview/EditReviewCompleteModal/EditReviewCompleteModal";
import ReviewCompleteCommentsModal from "./CoughReview/ReviewCompleteModal/ReviewCompleteCommentsModal";
import EditReOpenCommentsModal from "./CoughReview/EditReOpenCommentsModal/EditReOpenCommentsModal";
import ReOpenAnalysisSetModal from "./CoughReview/ReOpenAnalysisSetModal/ReOpenAnalysisSetModal";
import { AssignToVisualReviewerModal } from "../AssignToVisualReviewerModal/AssignToVisualReviewerModal";
import StartNewReviewModal from "./CoughReview/StartNewReviewModal/StartNewReviewModal";
import { DropdownCommentTypes } from "./CoughReview/DropdownCommentsModal/DropdownCommentsHelpers";
import { CoughAnalysisSetTypeEnum } from "../../misc/api/CoughAnalysisSetTypes";
import CoughAnalysisSetTypeDisplay from "./CoughType/CoughAnalysisSetTypeDisplay";
import CoughAnalysisSetUtilities from "../Shared/CoughAnalysisSetUtilities";
import ChangeCreatedFromModal from "../ChangeCreatedFromModal/ChangeCreatedFromModal";
import { ReImportDeviceEventsModal } from "../ReImportDeviceEventsModal/ReImportDeviceEventsModal";
import { Column, Table } from "../../Components/QueryTable";
import { useQuery, Condition as OQueryCondition } from "../../Components/Query";
import { MatchSortHeader } from "../../Components/MatchSortHeader";
import { CoughAnalysisSet, CoughSession, WebPortalClient } from "webportalclient";
import { useCurrentStudyConfigurationContext } from "../../Core/CurrentStudyConfigurationContext";
import { useReferenceContextProvider } from "../../Core/ReferenceContextProvider";
import LoadingWithMessage from "../../misc/components/LoadingWithMessage";
import { useSecurityContext } from "../../Core/SecurityContext";
import { ErrorBoundary } from "../../errors/ErrorBoundary";
import { DateMatchSortHeader } from "../../Components/DateMatchSortHeader";
import { useWebPortalApiClient, useWebPortalLegacyClient } from "../../misc/api/WebPortalTokenClient";
import { AnalysisDeadlinesModal } from "../AnalysisDeadlinesModal";
import { CreateProjectQABatchModal } from "../../cough/ProjectQA/CreateProjectQABatchModal";
import { WeeklyQAModal } from "../../cough/WeeklyQA";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBars } from '@fortawesome/free-solid-svg-icons';
import { Form } from "informedv4";
import { HamburgerMenu } from "../../misc/components/HamburgerMenu";


declare const rootUrl: string;

const AssignedUserCondition = (row: CoughAnalysisSet, currentUser: User): boolean => {
    if (row.analyst && row.analyst === (currentUser ? currentUser.Username : null)) {
        return true;
    } else {
        return false;
    }
};

const AssignedVisualReviewUserCondition = (row: CoughAnalysisSet, currentUser: User): boolean => {
    if (row.qaAnalyst && row.qaAnalyst === (currentUser ? currentUser.Username : null)) {
        return true;
    } else {
        return false;
    }
};

const ShouldRenderCreateAdditionalCoughCount = (row: CoughAnalysisSet) =>
    !row.isLinkedAnalysisSet &&
    (row.type === CoughAnalysisSetTypeEnum.Analysis || row.type === CoughAnalysisSetTypeEnum.AbacasCoughCount);

const getViewLink = (anSet: CoughAnalysisSet): JSX.Element => {
    const analysisSetId = anSet.isLinkedAnalysisSet ? CoughAnalysisSetUtilities.GetSourcedFromLink(anSet).targetId : anSet.id;
    if (!anSet.visualReview) {
        return <Link to={`/Cough/Analyse/${analysisSetId}/true`}>{anSet.isLinkedAnalysisSet ? "View Source Analysis Set" : "View"}</Link>
    }
}

const isCorrectTypeForReview = (anSet: CoughAnalysisSet) => !anSet.isLinkedAnalysisSet && anSet.type !== CoughAnalysisSetTypeEnum.DeviceEvents && anSet.type !== CoughAnalysisSetTypeEnum.CoughBouts && anSet.type !== CoughAnalysisSetTypeEnum.AbacasCoughCount;

const canCompleteReview = (anSet: CoughAnalysisSet): boolean => {
    return isCorrectTypeForReview(anSet) && !anSet.reviewComplete && anSet.reviewRequired && (anSet.status === OverreadStatusIdEnum.Accepted || anSet.status === OverreadStatusIdEnum.Rejected);
}

const canSendForReview = (anSet: CoughAnalysisSet): boolean => {
    return isCorrectTypeForReview(anSet) && !anSet.reviewComplete && !anSet.reviewRequired && (anSet.status === OverreadStatusIdEnum.Accepted || anSet.status === OverreadStatusIdEnum.Rejected);
}

const canEditReOpenComments = (anSet: CoughAnalysisSet): boolean => {
    return isCorrectTypeForReview(anSet) && !!anSet.hasReOpenComments && (!anSet.reviewRequired || (anSet.reviewRequired && anSet.reviewComplete));
}

const canEditReviewComments = (anSet: CoughAnalysisSet): boolean => {
    return isCorrectTypeForReview(anSet) && anSet.reviewComplete;
}

const canResetAnalysisSetReview = (anSet: CoughAnalysisSet): boolean => {
    return isCorrectTypeForReview(anSet) && anSet.reviewRequired && (anSet.status === OverreadStatusIdEnum.Accepted || anSet.status === OverreadStatusIdEnum.Rejected);
}

const canStartNewReview = (anSet: CoughAnalysisSet): boolean => {
    return anSet.reviewComplete && (anSet.status === OverreadStatusIdEnum.Accepted || anSet.status === OverreadStatusIdEnum.Rejected);
}

const canAssignForVisualReview = (anSet: CoughAnalysisSet): boolean => {
    return anSet.visualReview === true;
}

const initialDropDownCommentsState = {
    selectedDropdownCommentType: null,
    isAnalysisSetLinked: false,
    reOpenedDuringReview: false
}

export const CoughAnalysisQueryTable = (props: CoughAnalysisQueryTableProps) => {
    const client = useWebPortalApiClient();
    const { study } = useCurrentStudyConfigurationContext();
    const { subjectTypes, overreadStatuses, deviceTypes, coughWaveformTypes } = useReferenceContextProvider();
    const { currentUser, currentUserPermissions } = useSecurityContext();
    const [siteOptions, setSiteOptions] = React.useState([]);
    const [subjectOptions, setSubjectOptions] = React.useState([]);
    const [visitOptions, setVisitOptions] = React.useState([]);
    const [sessionOptions, setSessionOptions] = React.useState([]);
    const [serialNumbers, setSerialNumbers] = React.useState([]);
    const [analysts, setAnalysts] = React.useState([]);
    const [types, setTypes] = React.useState([]);
    const [historyViewVisible, setHistoryViewVisible] = React.useState(false);
    const [assignToUserVisible, setAssignToUserVisible] = React.useState(false);
    const [assignToVisualReviewerVisible, setAssignToVisualReviewerVisible] = React.useState(false);
    const [importEventsVisible, setImportEventsVisible] = React.useState(false);
    const [reImportDeviceEventsVisible, setReImportDeviceEventsVisible] = React.useState(false);
    const [manuallySendVisible, setManuallySendVisible] = React.useState(false);
    const [showDeadlinesModal, setShowDeadlinesModal] = React.useState(false);
    const [createLinkedVisible, setCreateLinkedVisible] = React.useState(false);
    const [removeLinkedVisible, setRemoveLinkedVisible] = React.useState(false);
    const [showChangeCreatedFromVisible, setShowChangeCreatedFromVisible] = React.useState(false);
    const [projectQASelectionModalVisible, setProjectQASelectionModalVisible] = React.useState(false);
    const [selectedAnalysisSet, setSelectedAnalysisSet] = React.useState<CoughAnalysisSet>();
    const [dropdownComments, setDropdownComments] = React.useState(initialDropDownCommentsState);
    const [sessionWaveforms, setSessionWaveforms] = React.useState<Pick<CoughSession, 'id' | 'waveform'>[]>([]);
    const [shouldSendAnalysisSetForReview, setshouldSendAnalysisSetForReview] = React.useState<boolean>(false);
    const [showAssignWeeklyQA, setShowAssignWeeklyQA] = React.useState<boolean>(false);

    const legacyClient = useWebPortalLegacyClient();

    const { data, applyFilters, filters, refresh } = useQuery();
    const location = useLocation();

    React.useEffect(() => {
        const getSites = async () => {
            const sites = await client.client.getSites("id,number", `studyId eq ${study.id}`, "number");
            const options = sites.value.map(s => ({
                value: s.id,
                label: s.number,
            }));

            setSiteOptions(options);
        };


        const getSubjects = async () => {
            const subjects = await client.client.getSubjects("id,number", `site/studyId eq ${study.id}`, "number");
            const options = subjects.value.map(s => ({
                value: s.id,
                label: s.number,
            }));

            setSubjectOptions(options);
        };

        const getVisits = async () => {
            const visits = await client.client.getCoughSessions("visit", `visit/subject/site/studyId eq ${study.id}`, "visit/name", "visit($select=name)");
            const options = uniqBy(visits.value, o => o.visit.name).map(s => ({
                value: s.visit.name,
                label: s.visit.name,
            }));

            setVisitOptions(options);
        };

        const getSessions = async () => {
            const sessions = await client.client.getCoughSessions("name", `visit/subject/site/studyId eq ${study.id}`, "name", undefined);
            const options = uniqBy(sessions.value, o => o.name).map(s => ({
                value: s.name,
                label: s.name,
            }));

            setSessionOptions(options);
        };

        const getDeviceSerials = async () => {
            const serialNumbers = await client.client.getCoughSessions("serialNumber", `visit/subject/site/studyId eq ${study.id}`, "serialNumber", undefined);
            const options = uniqBy(serialNumbers.value, o => o.serialNumber).map(s => ({
                value: s.serialNumber,
                label: s.serialNumber,
            }));

            setSerialNumbers(options);
        };

        const getAnalysts = async () => {
            const analysts = await client.client.getCoughAnalysisSets("analyst", `session/visit/subject/site/studyId eq ${study.id}`, "analyst");
            const options = uniqBy(analysts.value, o => o.analyst).filter(o => o.analyst).map(s => ({
                value: s.analyst.replace(/'/g, "''"),
                label: s.analyst,
            }));

            setAnalysts(options);
        };

        getSites();
        getSubjects();
        getVisits();
        getSessions();
        getDeviceSerials();
        getAnalysts();

    }, [study.id]);

    React.useEffect(() => {
        const getTypes = async () => {

            const types = await props.coughAnalysisSetTypeQuery.Data;
            if (types) {
                const options = types.map(s => ({
                    value: s.Id,
                    label: s.Name,
                }));

                setTypes(options);
            }
        };

        getTypes();
    }, [props.coughAnalysisSetTypeQuery.Data])

    React.useEffect(() => {
        const queryFilters = qs.parse(location.search);

        applyFilters([
            ...filters,
            ...(queryFilters.type ? [{ field: "type", condition: "eq" as OQueryCondition, values: [queryFilters.type] }] : []),
            ...(queryFilters.status ? [{ field: "status", condition: "eq" as OQueryCondition, values: [queryFilters.status] }] : [])
        ])
    }, [location.search]);

    React.useEffect(() => {
        dispatch(FetchCoughAnalysisSetTypes.create(legacyClient));
    }, []);

    React.useEffect(() => {
        const getWaveforms = async () => {
            const waveforms = [];
            for (const row of data) {
                const sessionWaveformResult = await client.client.getCoughSession(row.sessionId, "id,waveform");
                waveforms.push(sessionWaveformResult);
            }

            setSessionWaveforms(waveforms);
        }

        getWaveforms();
    }, [data])

    const handleEditReopenReason = (analysisSet: CoughAnalysisSet) => {
        setSelectedAnalysisSet(analysisSet);

        setDropdownComments({
            ...initialDropDownCommentsState,
            selectedDropdownCommentType: DropdownCommentTypes.EditReOpen,
            isAnalysisSetLinked: false,
            reOpenedDuringReview: false
        });
    };

    const handleCompleteReview = (analysisSet: CoughAnalysisSet, wasReopenedDuringReview: boolean, hasLinks: boolean) => {
        setSelectedAnalysisSet(analysisSet);

        setDropdownComments({
            ...initialDropDownCommentsState,
            selectedDropdownCommentType: DropdownCommentTypes.ReviewComplete,
            isAnalysisSetLinked: hasLinks,
            reOpenedDuringReview: wasReopenedDuringReview
        });
    }

    const handleEditCompleteReview = (analysisSet: CoughAnalysisSet, wasReopenedDuringReview: boolean) => {
        setSelectedAnalysisSet(analysisSet);

        setDropdownComments({
            ...initialDropDownCommentsState,
            selectedDropdownCommentType: DropdownCommentTypes.EditReviewComplete,
            isAnalysisSetLinked: false,
            reOpenedDuringReview: wasReopenedDuringReview
        });
    }

    const handleResetReview = (analysisSet: CoughAnalysisSet) => {
        setSelectedAnalysisSet(analysisSet);

        setDropdownComments({
            ...initialDropDownCommentsState,
            selectedDropdownCommentType: DropdownCommentTypes.ResetReview,
            reOpenedDuringReview: false
        });
    }

    const handleStartNewReview = (analysisSet: CoughAnalysisSet) => {
        setSelectedAnalysisSet(analysisSet);

        setDropdownComments({
            ...initialDropDownCommentsState,
            selectedDropdownCommentType: DropdownCommentTypes.StartNewReview,
            reOpenedDuringReview: false
        });
    }

    const ConfirmSendAnalysisSetForReview = ({ onYes }) => {
        return (
            <Modal show={shouldSendAnalysisSetForReview} header="Sending Analysis set for review">
                <div className="modal-body">
                    The Analysis set is about to be sent for review, proceed?
                </div>
                <div className="modal-footer">
                    <button className="btn btn-primary" data-dismiss="modal" onClick={() => onYes()}>Yes</button>
                    <button className="btn btn-primary btn-outline" data-dismiss="modal" onClick={() => setshouldSendAnalysisSetForReview(false)}>No</button>
                </div>
            </Modal>
        );
    }

    const handleChangeCreatedFromLink = (analysisSet: CoughAnalysisSet) => {
        setSelectedAnalysisSet(analysisSet);
        setShowChangeCreatedFromVisible(true);
    }

    const handleAssignToVisualReviewer = () => {
        setAssignToVisualReviewerVisible(true);

    }

    const RenderHamburgerMenu = (
        row: CoughAnalysisSet,
        handleReOpen: (analysisSet: CoughAnalysisSet, hasLinks: boolean, reOpenedDuringReview: boolean) => void,
        handleCompleteReview: (as: CoughAnalysisSet, wasReOpenedDuringReview: boolean, hasLinks: boolean) => void,
        handleEditCompleteReview: (as: CoughAnalysisSet, wasReOpenedDuringReview: boolean) => void,
        handleResetReview: (as: CoughAnalysisSet) => void,
        handleStartNewReview: (as: CoughAnalysisSet) => void,
        handleChangeCreatedFromLink: (as: CoughAnalysisSet) => void,
        handleAssignToVisualReviewer: (id: string) => void,
    ) => {
        const { study } = useCurrentStudyConfigurationContext();
        const { currentUserPermissions } = useSecurityContext();
        const [processingComplete, setProcessingComplete] = React.useState<boolean | undefined>();
        const [coughSession, setCoughSession] = React.useState<CoughSession>();

        React.useEffect(() => {
            const getCoughSession = async () => {
                const coughSessionResult = await client.client.getCoughSession(row.sessionId, "status,statusName");
                setCoughSession(coughSessionResult);
                const isProcessingComplete = [
                    CoughSessionStatusEnum.AwaitingQualityCheck as string,
                    CoughSessionStatusEnum.AwaitingAnalysis,
                    CoughSessionStatusEnum.AnalysisInProgress,
                    CoughSessionStatusEnum.AnalysisComplete,
                    CoughSessionStatusEnum.DeviceUnassigned
                ].includes(coughSessionResult.status);

                setProcessingComplete(isProcessingComplete);
            }

            getCoughSession();
        }, [row.sessionId]);

        const CreateAdditionalCoughCount = async (
            e: React.MouseEvent<HTMLAnchorElement>,
            analysisSet: CoughAnalysisSet,
        ) => {
            setTimeout(() => {
                e.currentTarget.onclick = null;
                e.currentTarget.innerText = "Loading...";
            }, 1);

            await client.client.postCoughAnalysisSet({
                sessionId: analysisSet.sessionId,
                type: CoughAnalysisSetTypeEnum.Analysis,
                status: OverreadStatusIdEnum.NotStarted,
            })

            window.location.reload();
        };

        if (!coughSession || !coughAnalysisSetTypeQuery.Data)
            return <Loading size={FontSize.OneX} />;

        if (processingComplete || coughSession.status === CoughSessionStatusEnum.QualityCheckRejected) {
            return (
                <ErrorBoundary>
                    <div className="text-center">
                        <HamburgerMenu>
                                <Authorization
                                    permissionId={PermissionEnum.ViewSingleCoughSession}
                                    studyId={study.id}
                                    currentPermissions={currentUserPermissions}
                                >
                                    <li>
                                        {getViewLink(row)}
                                    </li>
                                </Authorization>
                                <Authorization
                                    permissionId={PermissionEnum.OpenAssignedCoughAnalysisSets}
                                    studyId={study.id}
                                    currentPermissions={currentUserPermissions}
                                >
                                    <Condition
                                        test={() =>
                                            !row.isLinkedAnalysisSet && (row.status === OverreadStatusIdEnum.NotStarted ||
                                                row.status === OverreadStatusIdEnum.Pending) && !row.visualReview &&
                                            AssignedUserCondition(row, currentUser)
                                        }
                                    >
                                        <li>
                                            <Link to={`/Cough/Analyse/${row.id}/false`}>Open</Link>
                                        </li>
                                    </Condition>
                                </Authorization>
                                <Authorization
                                    permissionId={PermissionEnum.OpenAssignedCoughAnalysisSets}
                                    studyId={study.id}
                                    currentPermissions={currentUserPermissions}
                                >
                                    <Condition
                                        test={() =>
                                            row.visualReview &&
                                            AssignedVisualReviewUserCondition(row, currentUser)
                                        }
                                    >
                                        <li>
                                            <Link to={`/Cough/Analyse/${row.id}/true`}>Open Review</Link>
                                        </li>
                                    </Condition>
                                </Authorization>
                                <Authorization
                                    permissionId={PermissionEnum.ReOpenCoughAnalysisSets}
                                    studyId={study.id}
                                    currentPermissions={currentUserPermissions}
                                >
                                    <Condition
                                        test={() => !row.isLinkedAnalysisSet && row.type !== CoughAnalysisSetTypeEnum.CoughBouts
                                            && row.type !== CoughAnalysisSetTypeEnum.AbacasCoughCount
                                            && (row.status === OverreadStatusIdEnum.Accepted ||
                                                row.status === OverreadStatusIdEnum.Rejected)
                                        }
                                    >
                                        <li>
                                            <a onClick={() => handleReOpen(row, row.isSourceOfAnother, row.reOpenedDuringReview)}>Re-Open</a>
                                        </li>
                                    </Condition>
                                </Authorization>
                                <Authorization permissionId={PermissionEnum.AssignCoughSession} studyId={study.id} currentPermissions={currentUserPermissions}>
                                    <Condition test={() => !row.isLinkedAnalysisSet && row.type !== CoughAnalysisSetTypeEnum.CoughBouts && row.type !== CoughAnalysisSetTypeEnum.AbacasCoughCount}>
                                        <li>
                                            <a onClick={() => {
                                                setAssignToUserVisible(true);
                                                setSelectedAnalysisSet(row);
                                            }}>
                                                Assign To Analyst...
                                            </a>
                                        </li>
                                    </Condition>

                                </Authorization>
                                <Authorization permissionId={PermissionEnum.ImportCoughEvents} studyId={study.id} currentPermissions={currentUserPermissions}>
                                    <Condition test={() => !row.isLinkedAnalysisSet && (row.status === OverreadStatusIdEnum.NotStarted || row.status === OverreadStatusIdEnum.Pending)}>
                                        <li>
                                            <a onClick={() => {
                                                setImportEventsVisible(true);
                                                setSelectedAnalysisSet(row);
                                            }}>
                                                Import Cough Events...
                                            </a>
                                        </li>
                                    </Condition>

                            </Authorization>
                            <Authorization
                                permissionId={PermissionEnum.SendCoughSessionForInterVariabilityReview}
                                studyId={study.id}
                                currentPermissions={currentUserPermissions}
                            >
                                <Condition test={() => !row.isLinkedAnalysisSet && coughSession.status === CoughSessionStatusEnum.AnalysisComplete}>
                                    <li>
                                            <a onClick={() => {
                                                setManuallySendVisible(true);
                                                setSelectedAnalysisSet(row);
                                            }}>
                                                Send for QA Review...
                                            </a>
                                        </li>
                                    </Condition>
                                </Authorization>
                                <Authorization
                                    permissionId={PermissionEnum.SendForEarlyQAReview}
                                    studyId={study.id}
                                    currentPermissions={currentUserPermissions}
                                >
                                    <Condition test={() => !row.isLinkedAnalysisSet && row.type === CoughAnalysisSetTypeEnum.Analysis}>
                                        <li>
                                            <a onClick={() => {
                                                setManuallySendVisible(true);
                                                setSelectedAnalysisSet(row);
                                            }}>
                                                Send for Early QA Review...
                                            </a>
                                        </li>
                                    </Condition>
                                </Authorization>
                                <Authorization
                                    permissionId={PermissionEnum.AllowMultipleCoughCountsOfCoughAnalysisSets}
                                    studyId={study.id}
                                    currentPermissions={currentUserPermissions}
                                >
                                    <Condition test={() => ShouldRenderCreateAdditionalCoughCount(row)}>
                                        <li>
                                            <a
                                                onClick={async e =>
                                                    await CreateAdditionalCoughCount(
                                                        e,
                                                        row
                                                    )
                                                }
                                            >
                                                Create Additional Cough Count
                                            </a>
                                        </li>
                                    </Condition>
                                </Authorization>
                                <Authorization
                                    permissionId={PermissionEnum.ViewAnalysisSetHistory}
                                    studyId={study.id}
                                    currentPermissions={currentUserPermissions}
                                >
                                    <li>
                                        <a onClick={() => {
                                            setHistoryViewVisible(true);
                                            setSelectedAnalysisSet(row)
                                        }}>
                                            View Analysis Set History
                                        </a>
                                    </li>
                                </Authorization>
                                <Authorization permissionId={PermissionEnum.UpdateCoughAnalysisSetReopenReason}
                                    studyId={study.id}
                                    currentPermissions={currentUserPermissions}>
                                    <Condition test={() => canEditReOpenComments(row)}>
                                        <li>
                                            <a onClick={() => handleEditReopenReason(row)}>
                                                Update Reopen Reason
                                            </a>
                                        </li>
                                    </Condition>
                                </Authorization>
                                <Authorization permissionId={PermissionEnum.CreateLinkedCoughAnalysisSets}
                                    studyId={study.id}
                                    currentPermissions={currentUserPermissions}>
                                    <Condition test={() => !row.isLinkedAnalysisSet && coughAnalysisSetTypeQuery.Data.length > 0 && coughAnalysisSetTypeQuery.Data.find(t => t.Id === row.type).IsQA}>
                                        <li>
                                            <a onClick={() => { setCreateLinkedVisible(true); setSelectedAnalysisSet(row); }}>
                                                Create Linked QA Analysis Set
                                            </a>
                                        </li>
                                    </Condition>
                                </Authorization>
                                <Authorization permissionId={PermissionEnum.RemoveLinkedCoughAnalysisSets}
                                    studyId={study.id}
                                    currentPermissions={currentUserPermissions}>
                                    <Condition test={() => row.isLinkedAnalysisSet}>
                                        <li>
                                            <a onClick={() => { setRemoveLinkedVisible(true); setSelectedAnalysisSet(row); }}>
                                                Remove Linked Analysis Set
                                            </a>
                                        </li>
                                    </Condition>
                                </Authorization>
                                <Authorization permissionId={PermissionEnum.ReviewCoughAnalysisSet}
                                    studyId={study.id}
                                    currentPermissions={currentUserPermissions}>
                                    <Condition test={() => canSendForReview(row)}>
                                        <li>
                                            <a onClick={() => { setSelectedAnalysisSet(row); setshouldSendAnalysisSetForReview(true); }}>
                                                Review Required
                                            </a>
                                        </li>
                                    </Condition>
                                </Authorization>
                                <Authorization permissionId={PermissionEnum.ReviewCoughAnalysisSet}
                                    studyId={study.id}
                                    currentPermissions={currentUserPermissions}>
                                    <Condition test={() => canCompleteReview(row)}>
                                        <li>
                                            <a onClick={() => handleCompleteReview(row, row.reOpenedDuringReview, row.isSourceOfAnother)}>
                                                Complete Review
                                            </a>
                                        </li>
                                    </Condition>
                                </Authorization>
                                <Authorization permissionId={PermissionEnum.AssignCoughAnalysisSetVisualReview}
                                    studyId={study.id}
                                    currentPermissions={currentUserPermissions}>
                                    <Condition test={() => canAssignForVisualReview(row)}>
                                        <li>
                                            <a onClick={() => { handleAssignToVisualReviewer(row.id); setSelectedAnalysisSet(row); }}>
                                                Assign for Review...
                                            </a>
                                        </li>
                                    </Condition>
                                </Authorization>
                                <Authorization permissionId={PermissionEnum.ReviewCoughAnalysisSet}
                                    studyId={study.id}
                                    currentPermissions={currentUserPermissions}>
                                    <Condition test={() => canEditReviewComments(row)}>
                                        <li>
                                            <a onClick={() => handleEditCompleteReview(row, row.reOpenedDuringReview)}>
                                                Update Review Comments
                                            </a>
                                        </li>
                                    </Condition>
                                </Authorization>
                                <Authorization permissionId={PermissionEnum.ReviewCoughAnalysisSet}
                                    studyId={study.id}
                                    currentPermissions={currentUserPermissions}>
                                    <Condition test={() => canResetAnalysisSetReview(row)}>
                                        <li>
                                            <a onClick={() => handleResetReview(row)}>
                                                Reset Current Review
                                            </a>
                                        </li>
                                    </Condition>
                                </Authorization>
                                <Authorization permissionId={PermissionEnum.ReviewCoughAnalysisSet}
                                    studyId={study.id}
                                    currentPermissions={currentUserPermissions}>
                                    <Condition test={() => canStartNewReview(row)}>
                                        <li>
                                            <a onClick={() => handleStartNewReview(row)}>
                                                Start New Review
                                            </a>
                                        </li>
                                    </Condition>
                                </Authorization>
                                <Authorization permissionId={PermissionEnum.SetCoughAnalysisDeadlines}
                                    studyId={study.id}
                                    currentPermissions={currentUserPermissions}>
                                    <li>
                                        <a onClick={() => { setShowDeadlinesModal(true); setSelectedAnalysisSet(row); }}>
                                            Set Analysis Required Dates
                                        </a>
                                    </li>
                                </Authorization>
                                <Authorization
                                    permissionId={PermissionEnum.SendCoughSessionForInterVariabilityReview}
                                    studyId={study.id}
                                    currentPermissions={currentUserPermissions}
                                >
                                    <Condition test={() => !row.isLinkedAnalysisSet && CoughAnalysisSetUtilities.IsCreatedFromAnother(row)}>
                                        <li>
                                            <a onClick={() => handleChangeCreatedFromLink(row)}>
                                                Change Created From Link
                                            </a>
                                        </li>
                                    </Condition>
                                </Authorization>
                                <Authorization permissionId={PermissionEnum.ReImportDeviceEvents}
                                    studyId={study.id}
                                    currentPermissions={currentUserPermissions}>
                                    <Condition test={() => row.type === CoughAnalysisSetTypeEnum.DeviceEvents}>
                                        <li>
                                            <a onClick={() => { setReImportDeviceEventsVisible(true); setSelectedAnalysisSet(row); }}>
                                                Re-Import Subject Events
                                            </a>
                                        </li>
                                    </Condition>
                            </Authorization>
                        </HamburgerMenu>
                    </div>
                </ErrorBoundary>
            );
        } else {
            return <div className="text-center">{coughSession.statusName}</div>;
        }
    };

    const {
        coughAnalysisSetQuery,
        coughAnalysisSetTypeQuery,
        dispatch
    } = props;

    const formatReviewStatus = (value: CoughAnalysisSet) => {
        let name;
        let colour;

        if (!value.reviewStatus) {
            return "-";
        }

        switch (value.reviewStatus) {
            case "ReviewComplete":
                colour = 'success';
                name = 'Review Complete';
                break;
            case "ReviewRequired":
                colour = 'primary';
                name = 'Review Required';
                break;
            case "ReOpenedDuringReview":
                colour = 'primary';
                name = 'Review Required - ReOpened';
                break;
            case "ReviewCompleteReOpenedDuringReview":
                colour = 'success';
                name = 'Review Complete - ReOpened';
                break;
        }

        return (<span className={"label label-" + colour}>{name}</span>)
    }

    const handleReOpen = (analysisSet: CoughAnalysisSet, hasLinks: boolean, wasReopenedDuringReview: boolean) => {
        setSelectedAnalysisSet(analysisSet);

        setDropdownComments({
            ...initialDropDownCommentsState,
            reOpenedDuringReview: wasReopenedDuringReview,
            selectedDropdownCommentType: DropdownCommentTypes.ReOpen,
            isAnalysisSetLinked: hasLinks
        })
    }

    const clearAllFilters = () => {
        applyFilters([]);
    }

    const booleanOptions = [{ label: "Yes", value: true }, { label: "No", value: false }];

    const handleFilterClose = () => {
        setDropdownComments(initialDropDownCommentsState);
        refresh();
    }

    if (subjectTypes.length === 0 || overreadStatuses.length === 0)
        return <LoadingWithMessage message="Loading Cough Analysis Sets..." />

    const subjectTypesOptions = subjectTypes.map(s => ({
        value: s.id,
        label: s.name,
    }));

    const statusesOptions = overreadStatuses.map(os => ({
        value: os.id,
        label: os.name
    }));

    const waveformOptions = coughWaveformTypes.map(cwt => ({
        value: cwt.id,
        label: cwt.name
    }));

    const reviewOptions = [
        { value: "ReviewRequired", label: "Review Required" },
        { value: "ReviewComplete", label: "Review Complete" },
        { value: "ReOpenedDuringReview", label: "Review Required - ReOpened" },
        { value: "ReviewCompleteReOpenedDuringReview", label: "Review Complete - ReOpened" }
    ]

    const visualReviewOptions = [
        { value: AnalystDecisionStatus.NoChanges, label: "No Changes" },
        { value: AnalystDecisionStatus.ApplyChangesAccept, label: "Apply Changes & Accept" },
        { value: AnalystDecisionStatus.ApplyChangesFurtherReviewRequired, label: "Apply Changes, Further Review Required" },
    ]

    const formatWaveform = (session: CoughSession) =>
        (sessionWaveforms.length > 0 && coughWaveformTypes.find(cwt => cwt.id === sessionWaveforms.find(sw => sw.id === session.id)?.waveform)?.name) ?? "-";

    const formatAnalyst = analyst => analyst ? analyst : "-";

    const formatStatus = (row: CoughAnalysisSet) => {

        return (<>
            <AnalysisAcceptabilityStatus value={row.status} />
            <br />
            {formatVisualReviewStatus(row)}
        </>);
    }

    const formatVisualReviewStatus = (coughAnalysisSet: CoughAnalysisSet) => {
        if (coughAnalysisSet.visualReview === true) {
            if (coughAnalysisSet.type === CoughAnalysisSetTypeEnum.QualityCheck) {
                return (
                    <span className="label label-primary">Review</span>
                );
            }
            else {
                return (
                    <span className="label label-primary">QA Review</span>
                );
            }
        }
        else
            return null;
    }

    const formatVisualReviewDecision = (visualReviewStatus: number) => {
        let name;
        let colour;

        if (visualReviewStatus == null) {
            return "-";
        }

        switch (visualReviewStatus) {
            case 0:
                colour = 'success';
                name = 'No Changes';
                break;
            case 1:
                colour = 'warning';
                name = 'Apply Changes & Accept';
                break;
            case 2:
                colour = 'danger';
                name = 'Apply Changes, Further Review Required';
                break;
        }

        return (<span className={"label label-" + colour}>{name}</span>)
    }

    const ViewCoughAnalyst = () => {
        return currentUserPermissions.some(x => (x.Id === PermissionEnum.ViewCoughAnalyst));
    }

    return (
        <>
            <div className="row">
                <div className="col-md-6">
                    <h2 className="text-primary">Cough Analysis Sets</h2>
                </div>
                <div className="col-md-6">
                    <div className="h2 pull-right">
                        <button className="btn btn-primary" onClick={clearAllFilters}>
                            Clear All Filters
                        </button>
                        <Authorization currentPermissions={currentUserPermissions} permissionId={PermissionEnum.GenerateCoughProjectQA} studyId={study.id}>
                            &nbsp;
                            <button
                                className="btn btn-primary"
                                onClick={() => setProjectQASelectionModalVisible(true)}
                            >
                                Project QA Selection
                            </button>
                        </Authorization>
                        <Authorization currentPermissions={currentUserPermissions} permissionId={PermissionEnum.GenerateCoughWeeklyQA} studyId={study.id}>
                            &nbsp;
                            <button className="btn btn-primary" onClick={() => setShowAssignWeeklyQA(true)}>
                                Weekly QA Selection
                            </button>
                        </Authorization>
                    </div>
                </div>
            </div>
            <div className="row">
                <div className="col-md-12">
                    <div className="ibox">
                        <div className="ibox-content">
                            <div className="table-responsive" style={{ minHeight: "900px" }}>
                                <Table rowKeyField="id" data={data}>
                                    <Column field="session" header={<MatchSortHeader title="Subject ID" field="session/visit/subjectId" options={subjectOptions} sortField="session/visit/subject/number" />} formatter={session => session.visit.subject.number} />
                                    <Column field="session" header={<MatchSortHeader title="Visit" field="session/visit/name" options={visitOptions} sortField="session/visit/name" />} formatter={session => session.visit.name} />
                                    <Column field="session" header={<MatchSortHeader title="Session" field="session/name" options={sessionOptions} sortField="session/name" />} formatter={session => session.name} />
                                    <Column field="session" header={<MatchSortHeader title="Form" field="sessionWaveform" options={waveformOptions} sortField="sessionWaveform" />} formatter={(session) => formatWaveform(session)} />
                                    <Column field="" header={<MatchSortHeader title="Type" field="type" options={types} sortField="type" />} formatter={analysisSet => <CoughAnalysisSetTypeDisplay analysisSet={analysisSet} />} />
                                    <Column field="" header={<MatchSortHeader title="Status" field="status" options={statusesOptions} sortField="status" />} formatter={analysisSet => formatStatus(analysisSet)} />
                                    <Column field="" header={<MatchSortHeader title="Review" field="reviewStatus" options={reviewOptions} sortField="reviewStatus" />} formatter={analysisSet => formatReviewStatus(analysisSet)} />
                                    <Column field="" header={<MatchSortHeader title="QAR Status" field="visualReviewAnalystSelectionId" options={visualReviewOptions} sortField="visualReviewAnalystSelectionId" />} formatter={analysisSet => formatVisualReviewDecision(analysisSet.visualReviewAnalystSelectionId)} />
                                    {ViewCoughAnalyst() ?
                                        <Column field="analyst" header={<MatchSortHeader title="Analyst" field="analyst" options={analysts} sortField="analyst" />} formatter={analyst => formatAnalyst(analyst)} />
                                        : null}
                                    <Column field="qaAnalyst" header={<MatchSortHeader title="QAR Analyst" field="qaAnalyst" options={analysts} sortField="qaAnalyst" />} formatter={qaAnalyst => formatAnalyst(qaAnalyst)} />
                                    <Column field="qaAnalystStageDateTime" wrapText={true} header={<DateMatchSortHeader title="QAR Assigned" field="qaAnalystStageDateTime" sortField="qaAnalystStageDateTime" />} formatter={fadt => formatDateTime(fadt)} />
                                    <Column field="" wrapText={true} header={<DateMatchSortHeader title="Last Received" field="session/lastReceived" sortField="session/lastReceived" />} formatter={analysisSet => formatDateTime(analysisSet.session.lastReceived)} />
                                    <Column field="started" wrapText={true} header={<DateMatchSortHeader title="Analysis Started" field="started" sortField="started" />} formatter={started => formatDateTime(started)} />
                                    <Column field="firstComplete" wrapText={true} header={<DateMatchSortHeader title="First Completed" field="firstComplete" sortField="firstComplete" />} formatter={firstCompleted => formatDateTime(firstCompleted)} />
                                    <Column field="qaDeadline" wrapText={true} header={<DateMatchSortHeader title="QA Required" field="qaDeadline" sortField="qaDeadline" />} formatter={qaDeadline => formatDateTime(qaDeadline)} />
                                    <Column field="analysisDeadline" wrapText={true} header={<DateMatchSortHeader title="Analysis Required" field="analysisDeadline" sortField="analysisDeadline" />} formatter={analysisDeadline => formatDateTime(analysisDeadline)} />
                                    <Column field="" formatter={analysisSet => RenderHamburgerMenu(analysisSet,
                                        handleReOpen,
                                        handleCompleteReview,
                                        handleEditCompleteReview,
                                        handleResetReview,
                                        handleStartNewReview,
                                        handleChangeCreatedFromLink,
                                        handleAssignToVisualReviewer)}
                                    />
                                </Table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <ConfirmSendAnalysisSetForReview onYes={() => {
                legacyClient
                    .sendAnalysisSetForReview(selectedAnalysisSet.id)
                    .then(() => {
                        setshouldSendAnalysisSetForReview(false);
                        refresh();
                    })
            }} />

            {historyViewVisible ? <HistoryView analysisSet={selectedAnalysisSet} onClose={() => setHistoryViewVisible(false)} /> : null}
            {assignToUserVisible ? <AssignToUserModal analysisSet={selectedAnalysisSet} onClose={() => { setAssignToUserVisible(false); refresh(); }} /> : null}
            {importEventsVisible ? <ImportEventsWizard analysisSet={selectedAnalysisSet} onClose={() => setImportEventsVisible(false)} /> : null}
            {manuallySendVisible ? <ManuallySendForQAModal analysisSet={selectedAnalysisSet} onClose={() => { setManuallySendVisible(false); refresh(); }} /> : null}
            <Form validateOn="submit">
                {showDeadlinesModal ? <AnalysisDeadlinesModal analysisSet={selectedAnalysisSet} onClose={() => { setShowDeadlinesModal(false); refresh(); }} /> : null}
            </Form>
            {assignToVisualReviewerVisible ? <AssignToVisualReviewerModal analysisSet={selectedAnalysisSet} onClose={() => { setAssignToVisualReviewerVisible(false); refresh(); }} /> : null}
            <ReOpenAnalysisSetModal
                analysisSetId={selectedAnalysisSet && selectedAnalysisSet.id}
                onClose={handleFilterClose}
                {...dropdownComments}
            />
            <EditReOpenCommentsModal
                analysisSetId={selectedAnalysisSet && selectedAnalysisSet.id}
                onClose={handleFilterClose}
                {...dropdownComments}
            />
            <ReviewCompleteCommentsModal
                analysisSetId={selectedAnalysisSet && selectedAnalysisSet.id}
                onClose={handleFilterClose}
                {...dropdownComments}
            />
            <EditReviewCompleteModal
                analysisSetId={selectedAnalysisSet && selectedAnalysisSet.id}
                onClose={handleFilterClose}
                {...dropdownComments}
            />
            <ResetReviewModal
                analysisSetId={selectedAnalysisSet && selectedAnalysisSet.id}
                onClose={handleFilterClose}
                {...dropdownComments}
            />
            <StartNewReviewModal
                analysisSetId={selectedAnalysisSet && selectedAnalysisSet.id}
                onClose={handleFilterClose}
                {...dropdownComments}
            />
            {createLinkedVisible ?
                <CreateLinkedModal
                    onClose={() => {
                        setCreateLinkedVisible(false);
                        refresh();
                    }}
                    selectedAnalysisSet={selectedAnalysisSet && selectedAnalysisSet.id}
                /> : null}
            {removeLinkedVisible ?
                <RemoveLinkedModal onClose={() => {
                    setRemoveLinkedVisible(false);
                    refresh();
                }} selectedAnalysisSet={selectedAnalysisSet && selectedAnalysisSet.id} />
                : null}
            {showChangeCreatedFromVisible ? <ChangeCreatedFromModal analysisSetId={selectedAnalysisSet && selectedAnalysisSet.id}
                currentUsername={currentUser.Username}
                isVisible={showChangeCreatedFromVisible}
                onClose={() => {
                    setShowChangeCreatedFromVisible(false);
                    refresh();
                }}
            /> : null}
            {reImportDeviceEventsVisible ?
                <ReImportDeviceEventsModal
                    onClose={() => {
                        setReImportDeviceEventsVisible(false);
                        refresh();
                    }}
                    selectedAnalysisSet={selectedAnalysisSet && selectedAnalysisSet.id}
                /> : null}
            {showAssignWeeklyQA ? <WeeklyQAModal onClose={() => setShowAssignWeeklyQA(false)} /> : null}
            {projectQASelectionModalVisible ? <CreateProjectQABatchModal onSave={(result: boolean) => { }} onClose={() => setProjectQASelectionModalVisible(false)} /> : null}
        </>
    );
}

function mapStateToProps(state: MainState): CoughAnalysisQueryTableProps {
    return {
        currentUser: state.security.currentUser,

        coughAnalysisSetQuery: state.coughAnalysisScreen.coughAnalysisSetQuery,
        coughAnalysisSetTypeQuery: state.coughAnalysisScreen.coughAnalysisSetTypeQuery,
        sessionQuery: state.coughAnalysisScreen.sessionQuery,
        countryQuery: state.coughAnalysisScreen.countryQuery,
        deviceSerialQuery: state.coughAnalysisScreen.deviceSerialQuery,
        cardSerialQuery: state.coughAnalysisScreen.cardSerialQuery,
        waveformTypeQuery: state.coughAnalysisScreen.waveformTypeQuery,
        analystQuery: state.coughAnalysisScreen.analystQuery,
        overreadStatusQuery: state.coughAnalysisScreen.overreadStatusQuery,
    };
}

export default connect<CoughAnalysisQueryTableProps>(mapStateToProps)(CoughAnalysisQueryTable);
